import {
    uploadData,
    list,
    getUrl,
    remove,
    downloadData,
    getProperties,
    copy,
} from '@aws-amplify/storage';

/**
* 
const result = await uploadFileToAWSS3Bucket("questions", file);
console.log("Upload AWS result : ", result)

const listOfFiles = await listFilesFromAWSS3Bucket("questions");
console.log("LIST : ", listOfFiles);

const singleFile = await getSingleFileByNameFromAWSS3Bucket("questions", "Screenshot 2025-04-22 113502.png")
console.log("singleFile : ", singleFile);

const deleteFile = await deleteFileFromAWSS3Bucket("questions", "Screenshot 2025-04-22 113502.png")
console.log("deleteFile : ", deleteFile);

const downloadFile = await downloadFromAWSS3Bucket("questions", "Screenshot 2025-04-22 113502.png")
console.log("downloadFile : ", downloadFile);
*
**/

/** S3 keys with spaces/parentheses (common in WhatsApp files) often fail browser PUT/CORS. */
export const sanitizeS3FileName = (name = '') => {
    const trimmed = String(name || '').trim();
    const lastDot = trimmed.lastIndexOf('.');
    const hasExt = lastDot > 0 && lastDot < trimmed.length - 1;
    const stem = hasExt ? trimmed.slice(0, lastDot) : trimmed;
    const ext = hasExt ? trimmed.slice(lastDot + 1) : '';
    const safeStem = (stem || `file_${Date.now()}`)
        .normalize('NFKD')
        .replace(/[^\w.-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 120) || `file_${Date.now()}`;
    const safeExt = (ext || '').replace(/[^\w]/g, '').toLowerCase();
    return safeExt ? `${safeStem}.${safeExt}` : safeStem;
};

const resolveContentType = (file, safeName = '') => {
    if (file?.type) return file.type;
    const lower = String(safeName || file?.name || '').toLowerCase();
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.mp4')) return 'video/mp4';
    if (lower.endsWith('.webm')) return 'video/webm';
    if (lower.endsWith('.mp3')) return 'audio/mpeg';
    if (lower.endsWith('.wav')) return 'audio/wav';
    if (lower.endsWith('.m4a')) return 'audio/mp4';
    return 'application/octet-stream';
};

/**
 * Resolve CloudFront/public URL for a private object.
 * PutObject itself returns 200 with an empty body — URL must come from getUrl (same as CSV bulk).
 */
export const getSingleFileByNameFromAWSS3Bucket = async (path, fileName) => {
    if (!fileName) return null;
    try {
        const result = await getUrl({
            key: `${path}/${fileName}`,
            options: {
                accessLevel: 'private',
                // expiresIn: 300,
            },
        });
        const pathname = result?.url?.pathname || '';
        const href = typeof result?.url === 'string'
            ? result.url
            : (result?.url?.href || result?.url?.toString?.() || '');
        const url =
            generatePublicUrlForFileInAWSS3Bucket(pathname) ||
            generatePublicUrlForFileInAWSS3Bucket(href) ||
            (href ? href.split('?')[0] : '');
        console.log('File URL:', url);
        return url || null;
    } catch (error) {
        console.error('Error fetching file:', error);
        return null;
    }
};

// ✅ Upload file AWS S3
// Note: S3 PutObject success is often HTTP 200 with empty response body — that is normal.
// We resolve the public URL afterward via getUrl, same pattern as CSV/folder upload.
export const uploadFileToAWSS3Bucket = async (path, file, onProgress) => {
    console.log('File uploaded start normal');
    const safeName = sanitizeS3FileName(file?.name);
    const key = `${path}/${safeName}`;
    const contentType = resolveContentType(file, safeName);

    try {
        const task = uploadData({
            key,
            data: file,
            options: {
                contentType,
                accessLevel: 'private',
                onProgress: (progress) => {
                    try {
                        const { transferredBytes = 0, totalBytes = 0 } = progress || {};
                        if (onProgress && totalBytes) {
                            const percent = Number(((transferredBytes / totalBytes) * 100).toFixed(2));
                            onProgress(percent);
                        }
                    } catch (error) {
                        console.log("internal error : ",error)
                     }
                },
            },
        });

        // Amplify result after empty PutObject body: { key, eTag, ... }
        const result = await task.result;
        const finalKey = result?.key || key;
        const fileName = safeName || finalKey.split('/').pop();
        const url = await getSingleFileByNameFromAWSS3Bucket(path, fileName);

        console.log('File uploaded successfully:', { ...result, key: finalKey, fileName, url });

        return {
            ...result,
            key: finalKey,
            fileName,
            contentType,
            url,
        };
    } catch (err) {
        console.error('Error uploading file:', err);
        const message =
            err?.message ||
            err?.name ||
            'S3 upload failed. Check Cognito session and bucket CORS (AllowedMethods PUT, AllowedHeaders *).';
        throw new Error(message);
    }
};

// ✅ All files in specific directory AWS S3
export const listFilesFromAWSS3Bucket = async (path) => {
    try {
        const result = await list({
            //path: 'questions/',       // folder path (prefix)
            prefix: `${path}/`,
            options: {
                accessLevel: 'private', // match access level
                //maxKeys: 1000,        // optional: limit result count
                listAll: true
            }
        });

        console.log("Files in 'questions/':", result);
        return result; // array of files
    } catch (err) {
        console.error("Error listing files:", err);
    }
};

export const generatePublicUrlForFileInAWSS3Bucket = (pathname) => {
    if (!pathname) return '';

    if (pathname.includes("https://d3oxgt44kg2k0d.cloudfront.net")) return pathname;

    if (pathname.includes("https://image-bucket-nexiantian.s3.ap-south-1.amazonaws.com")) {
        const finalUrl = pathname?.replace("https://image-bucket-nexiantian.s3.ap-south-1.amazonaws.com", 'https://d3oxgt44kg2k0d.cloudfront.net');
        if (finalUrl?.includes("?")) {
            return finalUrl.split("?")[0];
        }
        return finalUrl;
    }
    else {
        return `https://d3oxgt44kg2k0d.cloudfront.net${pathname}`;
    }
}

export const deleteFileFromAWSS3Bucket = async (path, fileName) => {
    try {
        const result = await remove({
            key: `${path}/${fileName}`,
            accessLevel: 'private',
        });

        console.log("Deleted File URL:", result);
        return result;
    } catch (error) {
        console.error('Delete error:', error);
        return false;
    }
};

export const downloadFromAWSS3Bucket = async (path, fileName) => {
    try {
        const result = await downloadData({
            key: `${path}/${fileName}`,
            options: {
                accessLevel: 'private'
            },
        }).result;

        const blob = await result?.body?.blob();
        console.log('Downloaded file Blob:', blob);

        // If you want to display an image:
        const url = URL.createObjectURL(blob);
        console.log('Blob Preview URL:', url);
        return url;
    } catch (error) {
        console.error('Failed to download file from S3:', error);
    }
};

export const getFileMetadataFromAWSS3Bucket = async (path, fileName) => {
    try {
        const metadata = await getProperties({
            key: `${path}/${fileName}`,
            options: {
                accessLevel: 'private'
            },
        });
        console.log('File metadata:', metadata);
        return metadata;
    } catch (error) {
        console.error('Metadata fetch error:', error);
        return null;
    }
};

/**
 * 
 * @param {sourceKey, destinationKey} params
 * sourceKey => current key
 * destinationKey => destination key
 * @returns 
 */
export const copyFileInAWSS3Bucket = async ({ sourceKey, destinationKey }) => {
    try {
        const result = await copy({
            source: {
                key: sourceKey,
                options: {
                    accessLevel: 'private'
                },
            },
            destination: {
                key: destinationKey,
                options: {
                    accessLevel: 'private'
                },
            },
        });

        console.log('Copy successful:', result);
        return result;
    } catch (error) {
        console.error('Error copying file:', error);
        return null;
    }
};